import { AlertTriangle, ArrowLeft, CalendarRange, CheckCircle2, ClipboardEdit, Send, Settings2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsPlan, useAgents, useCatalogueIndicateurs, useFichePlan } from '@/api/hooks';
import { LIBELLE_ETAT_MAJ, LIBELLE_METHODE_PLAN, LIBELLE_STATUT_JALON, LIBELLE_STATUT_PLAN, LIBELLE_TYPE_CIBLE, type FichePlan, type JalonPlan, type MethodePlan, type MiseAJourPlan, type ParametragePlan, type StatutPlan, type TypeCiblePlan } from '@/api/types';
import { Chargement, ConfirmationDialog, ErreurBloc, Kpi, PageHeader, StatutBadge, Vide } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr, montant, nombre, pct } from '@/lib/utils';
import { Avancement, StatutPlanBadge } from './PlanListe';

const STATUTS_DECLARABLES: StatutPlan[] = ['NON_DEMARREE', 'EN_COURS', 'TERMINEE', 'SUSPENDUE', 'ANNULEE'];
const periodesSaisie = (): string[] => {
  const d = new Date();
  return [0, 1, 2, 3].map((i) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1)).toISOString().slice(0, 7));
};
const erreur = (e: unknown, defaut: string) => toast.error(e instanceof Error ? e.message : defaut);

function DialogueMiseAJour({ f, ouvert, onOpenChange }: { f: FichePlan; ouvert: boolean; onOpenChange: (o: boolean) => void }) {
  const actions = useActionsPlan();
  const e = f.element;
  const [periode, setPeriode] = useState(periodesSaisie()[0]!);
  const existante = f.misesAJour.find((m) => m.periode === periode && (m.etat === 'SAISIE' || m.etat === 'COMPLEMENT_DEMANDE'));
  const [statut, setStatut] = useState<StatutPlan>('EN_COURS');
  const [valeur, setValeur] = useState('');
  const [enCours, setEnCours] = useState('');
  const [jalons, setJalons] = useState<string[]>([]);
  const [realisations, setRealisations] = useState('');
  const [difficultes, setDifficultes] = useState('');
  const [actionsCorrectives, setActionsCorrectives] = useState('');
  useEffect(() => {
    setStatut(existante?.statutDeclare ?? (e.statut === 'PREVUE' ? 'EN_COURS' : e.statut === 'TERMINEE' ? 'TERMINEE' : 'EN_COURS'));
    setValeur(existante?.valeurIndicateur !== null && existante?.valeurIndicateur !== undefined ? String(existante.valeurIndicateur) : '');
    setEnCours(existante?.valeurEnCours ? String(existante.valeurEnCours) : '');
    setJalons(existante?.jalonsAtteints ?? f.jalons.filter((j) => j.statut !== 'A_VENIR').map((j) => j.id));
    setRealisations(existante?.realisations ?? '');
    setDifficultes(existante?.difficultes ?? '');
    setActionsCorrectives(existante?.actionsCorrectives ?? '');
  }, [existante, e.statut, f.jalons, periode]);
  const deja = f.misesAJour.find((m) => m.periode === periode && (m.etat === 'VALIDEE' || m.etat === 'SOUMISE'));
  const enregistrer = async (soumettre: boolean) => {
    try {
      await actions.miseAJour.mutateAsync({
        ligneId: e.ligneId,
        dto: {
          periode,
          statutDeclare: statut,
          valeurIndicateur: e.methode === 'INDICATEUR' && valeur !== '' ? Number(valeur.replace(',', '.')) : null,
          valeurEnCours: e.methode === 'INDICATEUR' && enCours !== '' ? Number(enCours.replace(',', '.')) : null,
          jalonsAtteints: e.methode === 'JALONS' ? jalons : [],
          realisations,
          difficultes,
          actionsCorrectives,
          soumettre,
        },
      });
      toast.success(soumettre ? 'Mise à jour soumise au directeur.' : 'Mise à jour enregistrée en saisie.');
      onOpenChange(false);
    } catch (x) {
      erreur(x, 'Enregistrement impossible.');
    }
  };
  const unite = e.cibleUnite ?? '';
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Mise à jour d’avancement · {e.code}</DialogTitle>
          <DialogDescription>
            {LIBELLE_METHODE_PLAN[e.methode]}
            {e.methode === 'INDICATEUR' && e.cibleValeur !== null && ` · cible ${nombre(e.typeCible === 'PERIODIQUE' ? e.ciblePeriode : e.cibleValeur)} ${unite}${e.typeCible === 'PERIODIQUE' ? ' par mois' : ''}`}. Les réalisations doivent être vérifiables : joindre les justificatifs sur la fiche. Un TDR, un FED ou un paiement ne change jamais le statut.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="maj-periode">Période</Label>
            <SelectNatif id="maj-periode" value={periode} onChange={(x) => setPeriode(x.target.value)}>
              {periodesSaisie().map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </SelectNatif>
            {deja && <p className="text-xs text-amber-700">Mise à jour de cette période déjà {deja.etat === 'VALIDEE' ? 'validée' : 'soumise'} : choisir une autre période.</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="maj-statut">Statut déclaré</Label>
            <SelectNatif id="maj-statut" value={statut} onChange={(x) => setStatut(x.target.value as StatutPlan)}>
              {STATUTS_DECLARABLES.map((s) => (
                <option key={s} value={s}>
                  {LIBELLE_STATUT_PLAN[s]}
                </option>
              ))}
            </SelectNatif>
          </div>
          {e.methode === 'INDICATEUR' && e.indicateurExterne && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Valeur de l’indicateur (lue depuis {e.indicateurExterne.sourceLibelle})</Label>
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <span className="num font-medium">{e.indicateurExterne.valeur === null ? 'non disponible' : nombre(e.indicateurExterne.valeur, 2)}</span> {unite}
                {e.indicateurExterne.actualiseLe && <span className="text-xs text-muted-foreground"> · actualisée le {dateHeureFr(e.indicateurExterne.actualiseLe)}</span>}
                <div className="text-xs text-muted-foreground">Enregistrée telle quelle avec la mise à jour, datée et marquée « source : {e.indicateurExterne.sourceLibelle} ».</div>
              </div>
            </div>
          )}
          {e.methode === 'INDICATEUR' && !e.indicateurExterne && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="maj-valeur">{e.typeCible === 'POURCENTAGE' ? 'Avancement (%)' : e.typeCible === 'PERIODIQUE' ? `Valeur de la période (${unite})` : `Réalisations réceptionnées (${unite})`}</Label>
                <Input id="maj-valeur" inputMode="decimal" value={valeur} onChange={(x) => setValeur(x.target.value)} placeholder="0" />
              </div>
              {e.typeCible !== 'POURCENTAGE' && (
                <div className="space-y-1.5">
                  <Label htmlFor="maj-encours">Parts réalisées des réalisations en cours</Label>
                  <Input id="maj-encours" inputMode="decimal" value={enCours} onChange={(x) => setEnCours(x.target.value)} placeholder="0,5 pour un ouvrage à moitié" />
                </div>
              )}
            </>
          )}
          {e.methode === 'JALONS' && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Jalons atteints (validés par le directeur)</Label>
              <div className="space-y-1 rounded-md border p-2">
                {f.jalons.map((j) => (
                  <label key={j.id} className={cn('flex items-center gap-2 text-sm', j.statut === 'ATTEINT_VALIDE' && 'text-muted-foreground')}>
                    <Checkbox checked={jalons.includes(j.id)} disabled={j.statut === 'ATTEINT_VALIDE'} onCheckedChange={(c) => setJalons((xs) => (c === true ? [...new Set([...xs, j.id])] : xs.filter((x) => x !== j.id)))} />
                    {j.ordre}. {j.libelle} <span className="text-xs text-muted-foreground">({nombre(j.poids)} pts{j.datePrevue ? `, prévu ${dateFr(j.datePrevue)}` : ''})</span>
                    {j.statut === 'ATTEINT_VALIDE' && <Badge variant="default">validé</Badge>}
                  </label>
                ))}
                {!f.jalons.length && <p className="text-xs text-muted-foreground">Aucun jalon paramétré.</p>}
              </div>
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="maj-real">Réalisations de la période</Label>
            <Textarea id="maj-real" rows={3} value={realisations} onChange={(x) => setRealisations(x.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="maj-diff">Difficultés</Label>
            <Textarea id="maj-diff" rows={2} value={difficultes} onChange={(x) => setDifficultes(x.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="maj-act">Actions correctives</Label>
            <Textarea id="maj-act" rows={2} value={actionsCorrectives} onChange={(x) => setActionsCorrectives(x.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant="outline" disabled={Boolean(deja) || actions.miseAJour.isPending} onClick={() => enregistrer(false)}>
            Enregistrer en saisie
          </Button>
          <Button disabled={Boolean(deja) || actions.miseAJour.isPending} onClick={() => enregistrer(true)}>
            <Send className="size-4" /> Soumettre à validation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueParametrage({ f, ouvert, onOpenChange }: { f: FichePlan; ouvert: boolean; onOpenChange: (o: boolean) => void }) {
  const actions = useActionsPlan();
  const e = f.element;
  const agents = useAgents(e.structureCode);
  const [methode, setMethode] = useState<MethodePlan>(e.methode);
  const [frequence, setFrequence] = useState(String(e.frequenceJours));
  const [typeCible, setTypeCible] = useState<TypeCiblePlan | ''>(e.typeCible ?? '');
  const [cible, setCible] = useState(e.cibleValeur === null ? '' : String(e.cibleValeur));
  const [ciblePeriode, setCiblePeriode] = useState(e.ciblePeriode === null ? '' : String(e.ciblePeriode));
  const [modePaiement, setModePaiement] = useState<'' | 'AVANCE' | 'LIVRAISON'>(e.modePaiement ?? '');
  const [poids, setPoids] = useState(e.poids === null ? '' : String(e.poids));
  const [responsable, setResponsable] = useState(e.responsable?.id ?? '');
  const catalogue = useCatalogueIndicateurs();
  const [codeExterne, setCodeExterne] = useState(f.lien?.code ?? '');
  const [modeExterne, setModeExterne] = useState<'PERIODE' | 'CUMUL'>(f.lien?.mode ?? 'CUMUL');
  const [jalons, setJalons] = useState<Array<{ id?: string; libelle: string; poids: string; datePrevue: string; valide: boolean }>>(f.jalons.map((j) => ({ id: j.id, libelle: j.libelle, poids: String(j.poids), datePrevue: j.datePrevue ?? '', valide: j.statut === 'ATTEINT_VALIDE' })));
  const sommePoids = jalons.reduce((s, j) => s + (Number(j.poids.replace(',', '.')) || 0), 0);
  const enregistrer = async () => {
    const dto: ParametragePlan = {
      methode,
      frequenceJours: Number(frequence) || 30,
      typeCible: methode === 'INDICATEUR' ? (typeCible || null) : null,
      cibleValeur: cible === '' ? null : Number(cible.replace(',', '.')),
      ciblePeriode: ciblePeriode === '' ? null : Number(ciblePeriode.replace(',', '.')),
      modePaiement: modePaiement || null,
      poids: poids === '' ? null : Number(poids.replace(',', '.')),
      responsableAgentId: responsable || null,
      jalons: methode === 'JALONS' ? jalons.map((j) => ({ id: j.id, libelle: j.libelle, poids: Number(j.poids.replace(',', '.')) || 0, datePrevue: j.datePrevue || null })) : undefined,
      indicateurExterne: codeExterne ? { code: codeExterne, mode: modeExterne } : null,
    };
    try {
      await actions.parametrer.mutateAsync({ ligneId: e.ligneId, dto });
      toast.success('Paramétrage du suivi enregistré.');
      onOpenChange(false);
    } catch (x) {
      erreur(x, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Paramétrage du suivi · {e.code}</DialogTitle>
          <DialogDescription>Méthode de mesure, fréquence, cible, jalons et poids, responsable opérationnel. La programmation héritée du PTBA (axe, objectif, dates, budget) n’est pas modifiable ici.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="par-methode">Méthode de mesure</Label>
            <SelectNatif id="par-methode" value={methode} onChange={(x) => setMethode(x.target.value as MethodePlan)}>
              {(Object.keys(LIBELLE_METHODE_PLAN) as MethodePlan[]).map((m) => (
                <option key={m} value={m}>
                  {LIBELLE_METHODE_PLAN[m]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="par-freq">Fréquence de mise à jour (jours)</Label>
            <Input id="par-freq" type="number" min={7} max={366} value={frequence} onChange={(x) => setFrequence(x.target.value)} />
          </div>
          {methode === 'INDICATEUR' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="par-type">Type de cible</Label>
                <SelectNatif id="par-type" value={typeCible} onChange={(x) => setTypeCible(x.target.value as TypeCiblePlan | '')}>
                  <option value="">Non précisé (cumulée)</option>
                  {(Object.keys(LIBELLE_TYPE_CIBLE) as TypeCiblePlan[]).map((t) => (
                    <option key={t} value={t}>
                      {LIBELLE_TYPE_CIBLE[t]}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="par-cible">Cible de l’exercice{e.cibleUnite ? ` (${e.cibleUnite})` : ''}</Label>
                <Input id="par-cible" inputMode="decimal" value={cible} onChange={(x) => setCible(x.target.value)} />
              </div>
              {typeCible === 'PERIODIQUE' && (
                <div className="space-y-1.5">
                  <Label htmlFor="par-periode">Cible d’une période (mois)</Label>
                  <Input id="par-periode" inputMode="decimal" value={ciblePeriode} onChange={(x) => setCiblePeriode(x.target.value)} />
                </div>
              )}
            </>
          )}
          {methode === 'INDICATEUR' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="par-externe">Indicateur lu depuis une application source</Label>
                <SelectNatif id="par-externe" value={codeExterne} onChange={(x) => setCodeExterne(x.target.value)}>
                  <option value="">Aucun (valeur saisie par le responsable)</option>
                  {(catalogue.data ?? []).filter((c) => !c.reserve).map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} · {c.libelle}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              {codeExterne && (
                <div className="space-y-1.5">
                  <Label htmlFor="par-mode-externe">Mode de lecture</Label>
                  <SelectNatif id="par-mode-externe" value={modeExterne} onChange={(x) => setModeExterne(x.target.value as 'PERIODE' | 'CUMUL')}>
                    <option value="CUMUL">Dernière valeur reçue (cumulée)</option>
                    <option value="PERIODE">Valeur de la période courante</option>
                  </SelectNatif>
                </div>
              )}
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="par-resp">Responsable opérationnel</Label>
            <SelectNatif id="par-resp" value={responsable} onChange={(x) => setResponsable(x.target.value)}>
              <option value="">Non désigné (émetteurs et chef de service de la structure)</option>
              {(agents.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.prenom} {a.nom} · {a.fonction}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="par-mode">Mode de paiement particulier</Label>
            <SelectNatif id="par-mode" value={modePaiement} onChange={(x) => setModePaiement(x.target.value as '' | 'AVANCE' | 'LIVRAISON')}>
              <option value="">Aucun (écart physique / financier examiné)</option>
              <option value="AVANCE">Avance de fonds</option>
              <option value="LIVRAISON">Paiement à la livraison</option>
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="par-poids">Poids dans l’activité (vide : égal)</Label>
            <Input id="par-poids" inputMode="decimal" value={poids} onChange={(x) => setPoids(x.target.value)} />
          </div>
          {methode === 'JALONS' && (
            <div className="space-y-2 sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Jalons pondérés (somme des poids = 100)</Label>
                <span className={cn('text-xs', Math.abs(sommePoids - 100) > 0.005 ? 'text-red-700' : 'text-emerald-700')}>somme {nombre(sommePoids)}</span>
              </div>
              {jalons.map((j, k) => (
                <div key={j.id ?? k} className="grid grid-cols-[1fr_5rem_9rem_auto] items-center gap-2">
                  <Input value={j.libelle} placeholder="Libellé du jalon" disabled={j.valide} onChange={(x) => setJalons((xs) => xs.map((y, i) => (i === k ? { ...y, libelle: x.target.value } : y)))} />
                  <Input inputMode="decimal" value={j.poids} placeholder="Poids" onChange={(x) => setJalons((xs) => xs.map((y, i) => (i === k ? { ...y, poids: x.target.value } : y)))} />
                  <Input type="date" value={j.datePrevue} onChange={(x) => setJalons((xs) => xs.map((y, i) => (i === k ? { ...y, datePrevue: x.target.value } : y)))} />
                  <Button type="button" variant="ghost" size="sm" disabled={j.valide} title={j.valide ? 'Jalon validé : conservé' : 'Retirer'} onClick={() => setJalons((xs) => xs.filter((_, i) => i !== k))}>
                    <XCircle className="size-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setJalons((xs) => [...xs, { libelle: '', poids: '', datePrevue: '', valide: false }])}>
                Ajouter un jalon
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button disabled={actions.parametrer.isPending} onClick={enregistrer}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueReprogrammation({ f, ouvert, onOpenChange }: { f: FichePlan; ouvert: boolean; onOpenChange: (o: boolean) => void }) {
  const actions = useActionsPlan();
  const e = f.element;
  const [debut, setDebut] = useState(e.debutPrevu ?? '');
  const [fin, setFin] = useState(e.finPrevue ?? '');
  const [motif, setMotif] = useState('');
  const envoyer = async () => {
    try {
      await actions.reprogrammer.mutateAsync({ ligneId: e.ligneId, dto: { nouveauDebut: debut || null, nouvelleFin: fin || null, motif } });
      toast.success(e.droits.valider ? 'Reprogrammation enregistrée.' : 'Reprogrammation proposée au directeur.');
      onOpenChange(false);
    } catch (x) {
      erreur(x, 'Reprogrammation impossible.');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reprogrammer · {e.code}</DialogTitle>
          <DialogDescription>Nouvelles dates prévues avec motif ; l’ancien calendrier et le retard antérieur restent dans l’historique. Le directeur valide la proposition du responsable.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="rep-debut">Nouveau début prévu</Label>
            <Input id="rep-debut" type="date" value={debut} onChange={(x) => setDebut(x.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rep-fin">Nouvelle fin prévue</Label>
            <Input id="rep-fin" type="date" value={fin} onChange={(x) => setFin(x.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="rep-motif">Motif</Label>
            <Textarea id="rep-motif" rows={3} value={motif} onChange={(x) => setMotif(x.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button disabled={motif.trim().length < 3 || actions.reprogrammer.isPending} onClick={envoyer}>
            <CalendarRange className="size-4" /> {e.droits.valider ? 'Reprogrammer' : 'Proposer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CarteMiseAJour({ m, f, jalons }: { m: MiseAJourPlan; f: FichePlan; jalons: JalonPlan[] }) {
  const actions = useActionsPlan();
  const e = f.element;
  const [decision, setDecision] = useState<'VALIDER' | 'COMPLEMENT' | null>(null);
  const soumettre = async () => {
    try {
      await actions.soumettre.mutateAsync(m.id);
      toast.success('Mise à jour soumise au directeur.');
    } catch (x) {
      erreur(x, 'Soumission impossible.');
    }
  };
  const decider = async (texte: string) => {
    try {
      await actions.decider.mutateAsync({ id: m.id, decision: decision!, commentaire: texte || undefined });
      toast.success(decision === 'VALIDER' ? 'Mise à jour validée : elle devient la situation de référence.' : 'Complément demandé au responsable.');
      setDecision(null);
    } catch (x) {
      erreur(x, 'Décision impossible.');
    }
  };
  const variante = m.etat === 'VALIDEE' ? 'default' : m.etat === 'SOUMISE' ? 'secondary' : m.etat === 'COMPLEMENT_DEMANDE' ? 'destructive' : 'outline';
  return (
    <div className={cn('rounded-lg border p-3 text-sm', m.etat === 'SOUMISE' && 'border-sky-200 bg-sky-50/40', m.etat === 'COMPLEMENT_DEMANDE' && 'border-amber-200 bg-amber-50/40')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{m.periode}</span>
          <Badge variant={variante}>{LIBELLE_ETAT_MAJ[m.etat]}</Badge>
          <StatutPlanBadge statut={m.statutDeclare} />
          {m.exception && <Badge variant="outline">saisie par exception</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">
          {m.auteurNom} · {dateFr(m.date)}
          {m.dateValidation && ` · ${m.etat === 'VALIDEE' ? 'validée' : 'renvoyée'} le ${dateHeureFr(m.dateValidation)} par ${m.valideParNom}`}
        </div>
      </div>
      <div className="mt-2 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        {m.valeurIndicateur !== null && (
          <div>
            <span className="text-muted-foreground">Valeur : </span>
            <span className="num font-medium">{nombre(m.valeurIndicateur, 2)}</span> {e.cibleUnite ?? ''}
            {m.valeurEnCours ? <span className="text-muted-foreground"> + {nombre(m.valeurEnCours, 2)} en cours</span> : null}
          </div>
        )}
        {m.jalonsAtteints && m.jalonsAtteints.length > 0 && (
          <div>
            <span className="text-muted-foreground">Jalons déclarés : </span>
            {m.jalonsAtteints.map((id) => jalons.find((j) => j.id === id)?.libelle ?? '?').join(', ')}
          </div>
        )}
        {m.realisations && (
          <div className="sm:col-span-2">
            <span className="text-muted-foreground">Réalisations : </span>
            {m.realisations}
          </div>
        )}
        {m.difficultes && (
          <div className="sm:col-span-2">
            <span className="text-muted-foreground">Difficultés : </span>
            {m.difficultes}
          </div>
        )}
        {m.actionsCorrectives && (
          <div className="sm:col-span-2">
            <span className="text-muted-foreground">Actions correctives : </span>
            {m.actionsCorrectives}
          </div>
        )}
        {m.commentaireValidateur && (
          <div className="sm:col-span-2">
            <span className="text-muted-foreground">Commentaire du validateur : </span>
            {m.commentaireValidateur}
          </div>
        )}
      </div>
      {((m.etat === 'SAISIE' || m.etat === 'COMPLEMENT_DEMANDE') && e.droits.mettreAJour) || (m.etat === 'SOUMISE' && e.droits.valider) ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {(m.etat === 'SAISIE' || m.etat === 'COMPLEMENT_DEMANDE') && e.droits.mettreAJour && (
            <Button size="sm" variant="outline" disabled={actions.soumettre.isPending} onClick={soumettre}>
              <Send className="size-4" /> Soumettre à validation
            </Button>
          )}
          {m.etat === 'SOUMISE' && e.droits.valider && (
            <>
              <Button size="sm" onClick={() => setDecision('VALIDER')}>
                <CheckCircle2 className="size-4" /> Valider{e.droits.exceptionValidation ? ' (par exception)' : ''}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDecision('COMPLEMENT')}>
                Demander un complément
              </Button>
            </>
          )}
        </div>
      ) : null}
      <ConfirmationDialog
        titre={decision === 'VALIDER' ? `Valider la mise à jour de ${m.periode}` : `Demander un complément pour ${m.periode}`}
        description={decision === 'VALIDER' ? 'La mise à jour validée devient la situation de référence de la sous-activité ; les jalons déclarés sont validés.' : 'La mise à jour est renvoyée au responsable avec votre motif.'}
        libelle={decision === 'VALIDER' ? 'Valider' : 'Renvoyer'}
        onConfirmer={decider}
        chargement={actions.decider.isPending}
        ouvert={decision !== null}
        onOpenChange={(o) => !o && setDecision(null)}
        champ={{ label: decision === 'VALIDER' ? 'Commentaire (facultatif)' : 'Complément demandé (obligatoire)', placeholder: decision === 'VALIDER' ? '' : 'Joindre le procès-verbal, préciser le nombre…' }}
      />
    </div>
  );
}

/** Fiche de suivi d'une sous-activité : programmation, avancement, jalons, mises à jour, reprogrammations, situations, documents liés. */
export function PlanFiche() {
  const { ligneId } = useParams<{ ligneId: string }>();
  const { data, isLoading, error } = useFichePlan(ligneId);
  const actions = useActionsPlan();
  const [miseAJour, setMiseAJour] = useState(false);
  const [parametrage, setParametrage] = useState(false);
  const [reprogrammation, setReprogrammation] = useState(false);
  const [decisionReprog, setDecisionReprog] = useState<{ id: string; decision: 'VALIDER' | 'REFUSER' } | null>(null);
  if (isLoading && !data) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const f: FichePlan = data;
  const e = f.element;
  const p = f.ligne.programmation;
  const deciderReprog = async (texte: string) => {
    try {
      await actions.deciderReprogrammation.mutateAsync({ id: decisionReprog!.id, decision: decisionReprog!.decision, commentaire: texte || undefined });
      toast.success(decisionReprog!.decision === 'VALIDER' ? 'Reprogrammation validée.' : 'Reprogrammation refusée.');
      setDecisionReprog(null);
    } catch (x) {
      erreur(x, 'Décision impossible.');
    }
  };
  const documents = [
    ...f.documents.tdr.map((d) => ({ ...d, type: 'TDR', lien: `/tdr/${d.id}` })),
    ...f.documents.fed.map((d) => ({ ...d, type: 'FED', lien: `/fed/${d.id}` })),
    ...f.documents.requisitions.map((d) => ({ ...d, type: 'Réquisition', lien: `/requisitions/${d.id}` })),
    ...f.documents.bons.map((d) => ({ ...d, type: 'Bon de commande', lien: `/achats/${d.id}` })),
  ];
  return (
    <div className="space-y-5">
      <PageHeader
        retour={
          <Link to={`/plan?exercice=${e.exercice}&structure=${e.structureCode}`} className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-3.5" /> Plan opérationnel
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {e.code} · {e.libelle} <StatutPlanBadge statut={e.statut} />
            {e.retard.enRetard && (
              <Badge variant="destructive" title={e.retard.motif ?? undefined}>
                <AlertTriangle className="size-3" /> retard {e.retard.jours} j
              </Badge>
            )}
            {!f.ligne.actif && <Badge variant="destructive">retirée de la version approuvée</Badge>}
          </span>
        }
        sousTitre={
          <span>
            {e.structureLibelle} · {e.activiteLibelle ?? 'Activité non classée'}
            {e.activiteCode && ` (${e.activiteCode})`}
            {p.axeCode && ` · ${p.axeCode}`}
            {p.objectifCode && ` · ${p.objectifCode}`}
            {f.ligne.version && ` · PTBA ${f.ligne.version.numero}`}
          </span>
        }
        actions={
          <>
            {e.droits.mettreAJour && f.ligne.actif && (
              <Button onClick={() => setMiseAJour(true)}>
                <ClipboardEdit className="size-4" /> Mettre à jour{e.droits.exceptionMiseAJour ? ' (par exception)' : ''}
              </Button>
            )}
            {(e.droits.mettreAJour || e.droits.valider) && f.ligne.actif && (
              <Button variant="outline" onClick={() => setReprogrammation(true)}>
                <CalendarRange className="size-4" /> Reprogrammer
              </Button>
            )}
            {e.droits.parametrer && (
              <Button variant="outline" onClick={() => setParametrage(true)}>
                <Settings2 className="size-4" /> Paramétrer
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Avancement physique" valeur={e.avancement === null ? 'non renseigné' : pct(e.avancement, 0)} detail={`${LIBELLE_METHODE_PLAN[e.methode]}${e.derniereValidation ? ` · validé le ${dateFr(e.derniereValidation)}` : ''}`} accent={e.avancement !== null && e.avancement >= 100 ? 'ok' : undefined} />
        <Kpi label="Taux d’exécution" valeur={pct(e.financier.tauxExecution, 0)} detail={`${montant(e.financier.paye)} payés sur ${montant(e.financier.budgetActualise)}`} />
        <Kpi label="Écart physique − financier" valeur={e.comparaison.ecart === null ? '—' : `${e.comparaison.ecart > 0 ? '+' : ''}${nombre(e.comparaison.ecart)} pts`} detail={e.comparaison.aExaminer ? 'à examiner (plus de 30 points)' : e.modePaiement ? (e.modePaiement === 'AVANCE' ? 'avance de fonds : écart attendu' : 'paiement à la livraison : écart attendu') : 'dans la norme'} accent={e.comparaison.aExaminer ? 'alerte' : undefined} />
        <Kpi label="Avancement calendaire" valeur={pct(e.comparaison.calendaire, 0)} detail={e.debutPrevu && e.finPrevue ? `${dateFr(e.debutPrevu)} → ${dateFr(e.finPrevue)}${e.reprogrammee ? ' (reprogrammé)' : ''}` : 'dates prévues non renseignées'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Programmation (PTBA)</CardTitle>
            <CardDescription>Attributs hérités du classeur approuvé ; paramétrage du suivi en bas.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Résultat attendu</dt>
              <dd>{p.resultatAttendu ?? '—'}</dd>
              <dt className="text-muted-foreground">Indicateur · cible</dt>
              <dd>
                {p.kpi ?? '—'}
                {p.cibleValeur !== null ? ` · ${nombre(p.cibleValeur)} ${p.cibleUnite ?? ''}` : p.cibleTexte ? ` · ${p.cibleTexte}` : ''}
                {e.indicateurExterne && (
                  <div className={cn('mt-1 rounded-md border px-2 py-1 text-xs', e.indicateurExterne.etat === 'A_JOUR' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : e.indicateurExterne.etat === 'NON_CONNECTE' ? 'border-dashed text-muted-foreground' : 'border-amber-200 bg-amber-50 text-amber-900')}>
                    Valeur lue depuis {e.indicateurExterne.sourceLibelle} · {e.indicateurExterne.libelle} ({e.indicateurExterne.mode === 'PERIODE' ? 'valeur de la période' : 'valeur cumulée'}) :{' '}
                    <span className="num font-medium">{e.indicateurExterne.valeur === null ? 'non disponible' : nombre(e.indicateurExterne.valeur, 2)}</span>
                    {e.indicateurExterne.actualiseLe && ` · actualisée le ${dateHeureFr(e.indicateurExterne.actualiseLe)}`} ·{' '}
                    {{ A_JOUR: 'à jour', OBSOLETE: 'obsolète', EN_ERREUR: 'en erreur', NON_CONNECTE: 'non connecté' }[e.indicateurExterne.etat]}
                    {e.indicateurExterne.message && ` · ${e.indicateurExterne.message}`}
                    <div className="text-[11px] opacity-80">Non modifiable dans FONAREV 360 ; le responsable complète seulement les réalisations, difficultés et pièces.</div>
                  </div>
                )}
              </dd>
              <dt className="text-muted-foreground">Livrable</dt>
              <dd>{p.livrable ?? '—'}</dd>
              <dt className="text-muted-foreground">Lieux</dt>
              <dd>{[p.lieux.join(', '), p.lieuPrecision].filter(Boolean).join(' · ') || '—'}</dd>
              <dt className="text-muted-foreground">Responsable</dt>
              <dd>
                {e.responsable?.nom || <span className="text-muted-foreground">non désigné</span>}
                {p.responsableLibelle && <span className="text-xs text-muted-foreground"> · classeur : {p.responsableLibelle}</span>}
                {p.partenaires && <div className="text-xs text-muted-foreground">partenaires : {p.partenaires}</div>}
              </dd>
              <dt className="text-muted-foreground">Dates prévues</dt>
              <dd>
                {dateFr(p.debutPrevu)} → {dateFr(p.finPrevue)}
                {e.reprogrammee && <div className="text-xs text-amber-700">reprogrammées : {dateFr(e.debutPrevu)} → {dateFr(e.finPrevue)}</div>}
              </dd>
              <dt className="text-muted-foreground">Suivi</dt>
              <dd>
                {LIBELLE_METHODE_PLAN[e.methode]}
                {e.methode === 'INDICATEUR' && e.typeCible && ` · ${LIBELLE_TYPE_CIBLE[e.typeCible]}`}
                {e.methode === 'INDICATEUR' && e.typeCible === 'PERIODIQUE' && e.ciblePeriode !== null && ` (${nombre(e.ciblePeriode)} par mois)`}
                {` · tous les ${e.frequenceJours} jours`}
                {e.poids !== null && ` · poids ${nombre(e.poids)}`}
                <div className="text-xs text-muted-foreground">
                  prochaine échéance {dateFr(e.echeance)}
                  {e.aMettreAJour && <span className="font-medium text-amber-700"> · mise à jour attendue</span>}
                  {e.aParametrer && <span className="font-medium text-amber-700"> · suivi à paramétrer</span>}
                </div>
              </dd>
            </dl>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Exécution financière</CardTitle>
            <CardDescription>Modules existants, sans ressaisie : budget, réservé, engagé, payé, disponible.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Kpi label="Budget prévisionnel" valeur={montant(e.financier.budgetActualise)} detail={e.financier.budgetInitial !== e.financier.budgetActualise ? `initial ${montant(e.financier.budgetInitial)}` : 'aucun virement'} />
              <Kpi label="Engagé" valeur={montant(e.financier.engage)} detail={pct(e.financier.tauxEngagement, 0)} />
              <Kpi label="En circuit" valeur={montant(e.financier.enCircuit)} detail="dossiers en validation" />
              <Kpi label="Disponible" valeur={montant(e.financier.disponible)} accent={e.financier.disponible < 0 ? 'critique' : undefined} />
            </div>
            {f.ligne.lignesDepense.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {f.ligne.lignesDepense.length} ligne(s) de dépense du PTBA ·{' '}
                <Link to={`/budget?exercice=${e.exercice}&structure=${e.structureCode}`} className="text-primary hover:underline">
                  détail dans le suivi budgétaire
                </Link>
              </div>
            )}
            <div>
              <div className="mb-1 text-sm font-medium">Documents liés</div>
              {!documents.length ? (
                <p className="text-xs text-muted-foreground">Aucun TDR, FED, réquisition ni bon de commande sur cette sous-activité.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {documents.slice(0, 20).map((d) => (
                    <li key={`${d.type}-${d.id}`} className="flex flex-wrap items-center gap-2">
                      <Link to={d.lien} className="font-medium hover:underline">
                        {d.type} {d.numero ?? '(brouillon)'}
                      </Link>
                      <span className="truncate text-muted-foreground">{d.objet}</span>
                      <StatutBadge statut={d.statut} />
                      <span className="num ml-auto text-xs">{montant(d.montant)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {e.methode === 'JALONS' && (
        <Card>
          <CardHeader>
            <CardTitle>Jalons</CardTitle>
            <CardDescription>Avancement = somme des poids des jalons validés par le directeur ; un jalon déclaré ne compte pas avant validation.</CardDescription>
          </CardHeader>
          <CardContent>
            {!f.jalons.length ? (
              <Vide message="Aucun jalon : paramétrer le suivi." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Jalon</TableHead>
                    <TableHead className="text-right">Poids</TableHead>
                    <TableHead>Prévu</TableHead>
                    <TableHead>Réalisé</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {f.jalons.map((j) => {
                    const echu = j.datePrevue && j.datePrevue < f.aujourdhui && j.statut !== 'ATTEINT_VALIDE';
                    return (
                      <TableRow key={j.id}>
                        <TableCell>{j.ordre}</TableCell>
                        <TableCell>{j.libelle}</TableCell>
                        <TableCell className="num text-right">{nombre(j.poids)}</TableCell>
                        <TableCell className={cn(echu && 'font-medium text-red-700')}>{dateFr(j.datePrevue)}</TableCell>
                        <TableCell>{dateFr(j.dateRealisee)}</TableCell>
                        <TableCell>
                          <Badge variant={j.statut === 'ATTEINT_VALIDE' ? 'default' : j.statut === 'ATTEINT_DECLARE' ? 'secondary' : 'outline'}>{LIBELLE_STATUT_JALON[j.statut]}</Badge>
                          {echu && <span className="ml-2 text-xs text-red-700">échu</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {e.typeCible === 'PERIODIQUE' && f.periodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Périodes</CardTitle>
            <CardDescription>Taux de chaque période écoulée (valeur validée ÷ cible de la période) ; deux périodes consécutives sous 50 % signalent un retard.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {f.periodes.map((x) => (
              <div key={x.periode} className={cn('rounded-md border px-2 py-1 text-xs', x.taux === null ? 'text-muted-foreground' : x.taux < 50 ? 'border-red-200 bg-red-50 text-red-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800')}>
                {x.periode} · {x.taux === null ? 'non renseignée' : pct(x.taux, 0)}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Mises à jour d’avancement</CardTitle>
          <CardDescription>Saisie par le responsable, soumise au directeur, validée ou renvoyée pour complément ; seules les mises à jour validées alimentent les tableaux de bord.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {!f.misesAJour.length ? <Vide message="Aucune mise à jour : la sous-activité est « Prévue »." /> : f.misesAJour.map((m) => <CarteMiseAJour key={m.id} m={m} f={f} jalons={f.jalons} />)}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Reprogrammations</CardTitle>
            <CardDescription>Ancien calendrier et retard antérieur conservés.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {!f.reprogrammations.length ? (
              <Vide message="Aucune reprogrammation." />
            ) : (
              f.reprogrammations.map((r) => (
                <div key={r.id} className="rounded-lg border p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={r.etat === 'VALIDEE' ? 'default' : r.etat === 'REFUSEE' ? 'destructive' : 'secondary'}>{r.etat === 'VALIDEE' ? 'Validée' : r.etat === 'REFUSEE' ? 'Refusée' : 'Proposée'}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {r.auteurNom} · {dateFr(r.date)}
                      </span>
                    </div>
                    {r.etat === 'PROPOSEE' && e.droits.valider && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => setDecisionReprog({ id: r.id, decision: 'VALIDER' })}>
                          Valider
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDecisionReprog({ id: r.id, decision: 'REFUSER' })}>
                          Refuser
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 text-xs">
                    {dateFr(r.ancienDebut)} → {dateFr(r.ancienneFin)} <span className="text-muted-foreground">devient</span> {dateFr(r.nouveauDebut)} → {dateFr(r.nouvelleFin)}
                    {r.retardAnterieurJours > 0 && <span className="text-muted-foreground"> · retard antérieur {r.retardAnterieurJours} j</span>}
                  </div>
                  <div className="mt-1 text-xs">{r.motif}</div>
                  {r.commentaire && <div className="mt-1 text-xs text-muted-foreground">{r.valideParNom} : {r.commentaire}</div>}
                </div>
              ))
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Situations mensuelles figées</CardTitle>
            <CardDescription>Instantané de clôture de chaque mois.</CardDescription>
          </CardHeader>
          <CardContent>
            {!f.situations.length ? (
              <Vide message="Aucune situation figée." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mois</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Avancement</TableHead>
                    <TableHead className="text-right">Retard</TableHead>
                    <TableHead className="text-right">Exécution</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {f.situations.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.periode}</TableCell>
                      <TableCell>
                        <StatutPlanBadge statut={s.statut} />
                      </TableCell>
                      <TableCell>
                        <Avancement valeur={s.avancement} petite />
                      </TableCell>
                      <TableCell className="num text-right">{s.retardJours ? `${s.retardJours} j` : '—'}</TableCell>
                      <TableCell className="num text-right">{pct(s.tauxExecution, 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <PiecesJointes objetType="PLAN" objetId={e.ligneId} typesSuggeres={['JUSTIFICATIF', 'AUTRE']} titre="Pièces justificatives" description="Procès-verbaux de réception, rapports de mission, photos : justificatifs des réalisations déclarées." />

      {miseAJour && <DialogueMiseAJour f={f} ouvert={miseAJour} onOpenChange={setMiseAJour} />}
      {parametrage && <DialogueParametrage f={f} ouvert={parametrage} onOpenChange={setParametrage} />}
      {reprogrammation && <DialogueReprogrammation f={f} ouvert={reprogrammation} onOpenChange={setReprogrammation} />}
      <ConfirmationDialog
        titre={decisionReprog?.decision === 'VALIDER' ? 'Valider la reprogrammation' : 'Refuser la reprogrammation'}
        description={decisionReprog?.decision === 'VALIDER' ? 'Les nouvelles dates prévues deviennent la référence du suivi ; l’historique conserve l’ancien calendrier.' : 'La proposition est refusée ; le responsable en est informé.'}
        libelle={decisionReprog?.decision === 'VALIDER' ? 'Valider' : 'Refuser'}
        onConfirmer={deciderReprog}
        chargement={actions.deciderReprogrammation.isPending}
        ouvert={decisionReprog !== null}
        onOpenChange={(o) => !o && setDecisionReprog(null)}
        champ={{ label: 'Commentaire (facultatif)' }}
      />
    </div>
  );
}
